/**
 * Business Brain — types + API bindings (Patch 34.1.5).
 *
 * The Business Brain lives ABOVE the Brand Brain in the intelligence
 * hierarchy. It answers "who are we, what do we believe, and why do
 * we exist" — the Brand Brain answers "how do we sound".
 *
 * Feature code MUST NOT read Business Brain directly for AI hydration.
 * Content Studio consumes the Canonical Intelligence interface. This
 * module exists only for the Founder Review UI.
 */
import { http } from "@/shared/api/http";

export type BusinessBrainStatus = "draft" | "pending_review" | "approved";

export interface MissionVision {
  mission: string;
  vision: string;
  purpose: string;
}
export interface Positioning {
  one_liner: string;
  category: string;
  for_whom: string;
  against_whom: string;
  unique_promise: string;
  proof_points: string[];
}
export interface CoreBeliefs {
  beliefs: string[];
  manifesto: string;
}
export interface IdealCustomerProfile {
  label: string;
  description: string;
  pains: string[];
  desires: string[];
  disqualifiers: string[];
}
export interface IdealCustomers {
  primary: IdealCustomerProfile;
  secondary: IdealCustomerProfile;
  non_customers: string[];
}
export interface Differentiators {
  differentiators: string[];
  moats: string[];
  inflection_bets: string[];
}
export interface CompetitivePhilosophy {
  stance: string;
  principles: string[];
  forbidden_moves: string[];
}
export interface FounderPrinciples {
  principles: string[];
  non_negotiables: string[];
  decision_lenses: string[];
}
export interface CommunicationStyle {
  default_register: string;
  signature_moves: string[];
  rhetorical_devices: string[];
  forbidden_registers: string[];
}
export interface GlossaryTerm {
  term: string;
  approved_definition: string;
  banned_synonyms: string[];
}
export interface Language {
  always_say: string[];
  never_say: string[];
  glossary: GlossaryTerm[];
}
export interface ProductLine {
  name: string;
  slug: string;
  description: string;
  products: string[];
  stage: string;
}
export interface ProductTaxonomy {
  primary_offer: string;
  revenue_model: string;
  lines: ProductLine[];
}

export interface BusinessBrain {
  id: string;
  workspace_id: string;
  status: BusinessBrainStatus;
  version: number;
  approved_at: string | null;
  approved_by: string | null;
  source: string;
  mission_vision: MissionVision;
  positioning: Positioning;
  core_beliefs: CoreBeliefs;
  ideal_customers: IdealCustomers;
  differentiators: Differentiators;
  competitive_philosophy: CompetitivePhilosophy;
  founder_principles: FounderPrinciples;
  communication_style: CommunicationStyle;
  language: Language;
  product_taxonomy: ProductTaxonomy;
}

export type BusinessBrainSection =
  | "mission_vision"
  | "positioning"
  | "core_beliefs"
  | "ideal_customers"
  | "differentiators"
  | "competitive_philosophy"
  | "founder_principles"
  | "communication_style"
  | "language"
  | "product_taxonomy";

export interface IntelligenceStatus {
  business_brain_status: string;
  brand_brain_status: string;
  business_brain_version: number | null;
  approved: boolean;
  missing: string[];
  hydration_ready: boolean;
  live_generation_ready: boolean;
}

export interface HICEvent {
  _id?: string;
  id?: string;
  workspace_id: string;
  brain_type: "business_brain" | "brand_brain";
  kind:
    | "section_edited"
    | "submitted_for_review"
    | "approved"
    | "reverted"
    | "genesis_seeded";
  actor_id: string | null;
  section: string | null;
  at: string;
}

// ── API bindings ─────────────────────────────────────────────────
export const fetchBusinessBrain = (workspaceId: string): Promise<BusinessBrain> =>
  http<BusinessBrain>(`/workspaces/${workspaceId}/business-brain`);

export const patchBusinessBrainSection = (
  workspaceId: string,
  section: BusinessBrainSection,
  data: Record<string, unknown>,
): Promise<BusinessBrain> =>
  http<BusinessBrain>(`/workspaces/${workspaceId}/business-brain`, {
    method: "PATCH",
    body: { section, data },
  });

export const submitBusinessBrainForReview = (
  workspaceId: string,
): Promise<BusinessBrain> =>
  http<BusinessBrain>(
    `/workspaces/${workspaceId}/business-brain/submit-for-review`,
    { method: "POST" },
  );

export const approveBusinessBrain = (workspaceId: string): Promise<BusinessBrain> =>
  http<BusinessBrain>(`/workspaces/${workspaceId}/business-brain/approve`, {
    method: "POST",
  });

export const fetchIntelligenceStatus = (
  workspaceId: string,
): Promise<IntelligenceStatus> =>
  http<IntelligenceStatus>(`/workspaces/${workspaceId}/intelligence/status`);

export const fetchHICEvents = (
  workspaceId: string,
  limit = 50,
): Promise<HICEvent[]> =>
  http<HICEvent[]>(
    `/workspaces/${workspaceId}/intelligence/hic-events?limit=${limit}`,
  );
